import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  investmentServices,
  investorServices,
  kycServices,
  opportunityServices,
} from '../../services'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { isActionAllowed } from '../../lib/permissions/adminActions'
import {
  getFileKeyFromSignedUrl,
  getOrderStatusErrorMessage,
  isValidDocType,
  isValidFileSize,
  isValidImageType,
  isValidVideoType,
  parseToCents,
} from '../../lib/utils/helpers'
import { showBanner } from '../../global-state/banner-slice'
import { InvestmentDetailPresenter } from './investment-detail-presenter'
import { InvestorBankDetails, InvestorProps, KYCDetailsWithDocument } from '../../lib/types'

const InvestmentDetailContainer = () => {
  const dispatch = useDispatch()
  const { actionGrants } = useSelector((s: RootState) => s.gbcUser)
  const allowInvestmentOrderStatusUpdates = isActionAllowed(
    actionGrants,
    'updateInvestmentOrderStatus',
  )
  const { projectId, investmentId, investorId } = useParams()
  const [project, setProject] = useState<Record<string, any>>({})
  const [investmentDetail, setInvestmentDetail] = useState<any>({})
  const [kycData, setKycData] = useState<KYCDetailsWithDocument>()
  const [investorProfile, setInvestorProfile] = useState<InvestorProps>()
  const [statuses, setStatuses] = useState([])
  const [isStatusUpdating, setIsStatusUpdating] = useState(false)
  const [investorBankDetails, setInvestorBankDetails] = useState<InvestorBankDetails>(
    {} as InvestorBankDetails,
  )
  const [tradeExecutedAuditLogs, setTradeExecutedAuditLogs] = useState<Record<string, unknown>[]>(
    [],
  )

  const fetchTradeExecutedAuditLogs = useCallback(async () => {
    if (!investmentId) {
      return
    }
    const res = await investmentServices.getInvestmentAuditLog(investmentId as string)
    if (res?.status === 200 && Array.isArray(res.data)) {
      setTradeExecutedAuditLogs(res.data)
    }
  }, [investmentId])

  const fetchInvestmentDetail = async () => {
    investmentServices.getInvestmentById(investmentId as string).then((response: any) => {
      if (response && response.data && response.status === 200) {
        const opportunityData = {
          opportunity_title: response.data.opportunity_title,
          asset_class: response.data.asset_class,
          ticket_size: response.data.ticket_size,
          investment_return: response.data.investment_return,
          raise_goal: response.data.raise_goal,
          currency_code: response.data.currency_code,
          reference_valuation:
            response.data.reference_valuation ??
            response.data.opportunity_metadata?.reference_valuation ??
            null,
        }
        setInvestmentDetail(response.data)
        setProject((prev) => ({ ...prev, ...opportunityData }))

        if (response.data.opportunity_id) {
          const fetchOpportunity = opportunityServices.getOpportunityById
          if (typeof fetchOpportunity === 'function') {
            void Promise.resolve(fetchOpportunity(String(response.data.opportunity_id), true))
              .then((oppRes: any) => {
                if (oppRes?.status === 200 && oppRes.data) {
                  setProject((prev) => ({
                    ...prev,
                    currency_code: oppRes.data.currency_code ?? prev.currency_code,
                    reference_valuation: oppRes.data.metadata?.reference_valuation ?? null,
                  }))
                }
              })
              .catch(() => undefined)
          }
        }

        investorServices
          .getInvestorProfile(response.data.investor_id)
          .then((response: InvestorProps) => {
            setInvestorProfile(response)
          })

        if (response.data.status === 'TRADE_EXECUTED' && investmentId) {
          const fetchAudit = investmentServices.getInvestmentAuditLog
          if (typeof fetchAudit === 'function') {
            void Promise.resolve(fetchAudit(investmentId as string)).then((auditRes: any) => {
              if (auditRes?.status === 200 && Array.isArray(auditRes.data)) {
                setTradeExecutedAuditLogs(auditRes.data)
              }
            })
          }
        } else {
          setTradeExecutedAuditLogs([])
        }
      }
    })
  }

  const getInvestorBankDetails = async () => {
    const response = await kycServices.getInvestorBankDetails(String(investorId))
    if (!!response && response.data) {
      setInvestorBankDetails(response.data)
    }
  }

  useEffect(() => {
    if (projectId && investmentId) {
      fetchInvestmentDetail()
    }
  }, [investmentId, projectId])

  useEffect(() => {
    const getKYC = async () => {
      const response = await kycServices.getDocumentWithKYC(String(investorId))
      if (!!response && response.data) {
        setKycData(response.data ?? {})
      }
    }

    if (investorId) {
      getKYC()
      getInvestorBankDetails()
    }
  }, [])

  const handleFilePreview = async (fileKey: string) => {
    fileKey = (fileKey as any).replaceAll('/', '%2F')

    const signedUrl = getFileKeyFromSignedUrl(fileKey)
    const data = await opportunityServices.previewFile(signedUrl)
    if (data && data.data && data.status === 200) return data.data.url
    else {
      dispatch(
        showBanner({
          text: (data.data && data.data.message) || 'Something went wrong preview document',
          variant: 'error-banner',
        }),
      )
      return ''
    }
  }

  const handleFileUpload = async (fileData: File, fileType: 'image' | 'doc' | 'video') => {
    const isValidType = () => {
      switch (fileType) {
        case 'doc':
          return isValidDocType(fileData)
        case 'video':
          return isValidVideoType(fileData)
        case 'image':
        default:
          return isValidImageType(fileData)
      }
    }
    if (isValidType() && isValidFileSize(fileData, fileType)) {
      const data: any = await investmentServices.uploadAllotmentConfirmation(fileData)
      if (data) {
        return data
      }
      return { message: 'Could not upload file' }
    }
    dispatch(
      showBanner({
        text: 'File size or type is incorrect for field',
        variant: 'error-banner',
      }),
    )
  }

  const handleUpdateStatus = async (payload: any) => {
    setIsStatusUpdating(true)
    try {
      const response = await investmentServices.updateInvestmentStatus(payload)
      if (response.data && response.status === 200) {
        dispatch(
          showBanner({
            text: response.data && response.data.message,
            variant: 'success-banner',
          }),
        )

        await fetchInvestmentDetail()
      } else
        dispatch(
          showBanner({
            text: getOrderStatusErrorMessage(
              response,
              'Something went wrong in updating investment status',
            ),
            variant: 'error-banner',
          }),
        )
    } finally {
      setIsStatusUpdating(false)
    }
  }

  const handleStatus = async ({
    value,
    field,
    description,
    units,
    tradeExecutionDate,
    actualInvestedAmount,
    allotmentConfirmation,
    rateOfConversion,
    skipEmail,
  }: {
    value: string | number
    field: string
    description: string
    units: number
    tradeExecutionDate?: Date
    actualInvestedAmount?: string
    allotmentConfirmation?: string
    rateOfConversion?: number
    skipEmail?: boolean
  }) => {
    let payload: Record<string, unknown> = {
      investmentId: investmentId,
      [field]: value,
    }

    if (field === 'investmentStatus') {
      payload = {
        ...payload,
        status: value,
        ...(skipEmail ? { skipEmail: true } : {}),
      }
    }

    if (field === 'investmentStatus' && value === 'REJECTED') {
      payload = {
        ...payload,
        description,
      }
    }
    if (field === 'investmentStatus' && value === 'TRADE_EXECUTED') {
      payload = {
        ...payload,
        units,
        tradeExecutionDate: (tradeExecutionDate ?? new Date()).toISOString(),
        ...(actualInvestedAmount
          ? { actualInvestedAmount: parseToCents(actualInvestedAmount) }
          : {}),
        ...(allotmentConfirmation ? { allotmentConfirmation } : {}),
        ...(rateOfConversion !== undefined && rateOfConversion !== null
          ? { rateOfConversion }
          : {}),
      }
    }
    await handleUpdateStatus(payload)
  }

  const handleUpdateTradeExecutedDetails = async (payload: {
    tradeExecutionDate?: string
    amountCents?: number
    units?: number
    tradeReferenceDetails?: string
    allotmentConfirmation?: string
    skipEmail?: boolean
  }) => {
    const response = await investmentServices.updateTradeExecutedInvestmentDetails({
      id: Number(investmentId),
      ...payload,
    })
    if (response?.status === 200) {
      dispatch(
        showBanner({
          text: response.data?.message ?? 'Trade details updated',
          variant: 'success-banner',
        }),
      )
      await fetchInvestmentDetail()
      await fetchTradeExecutedAuditLogs()
    } else {
      dispatch(
        showBanner({
          text: getOrderStatusErrorMessage(
            response,
            'Something went wrong while updating trade executed details',
          ),
          variant: 'error-banner',
        }),
      )
    }
  }

  return (
    <InvestmentDetailPresenter
      project={project}
      kycData={kycData}
      investorProfile={investorProfile}
      statuses={statuses}
      investorBankDetails={investorBankDetails}
      investmentDetail={investmentDetail}
      handleFilePreview={handleFilePreview}
      handleFileUpload={handleFileUpload}
      handleStatus={handleStatus}
      isStatusUpdating={isStatusUpdating}
      tradeExecutedAuditLogs={tradeExecutedAuditLogs}
      handleUpdateTradeExecutedDetails={handleUpdateTradeExecutedDetails}
      allowInvestmentOrderStatusUpdates={allowInvestmentOrderStatusUpdates}
    />
  )
}

export { InvestmentDetailContainer }
